import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../../services/api";

const API = `${API_BASE_URL}/api/pincodes`;
const CITY_API = `${API_BASE_URL}/api/cities`;

export default function PincodeManagement() {
  const [pincodes, setPincodes] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ pincode: "", city: "", state: "", area: "" });
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');

  // Fetch Pincodes
  const getPincodes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API);
      const sortedData = res.data.data.sort((a, b) => {
        const pinA = parseInt(a.pincode, 10);
        const pinB = parseInt(b.pincode, 10);
        return sortOrder === 'asc' ? pinA - pinB : pinB - pinA;
      });
      setPincodes(sortedData);
    } catch (err) {
      console.error("Error fetching pincodes:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Sort Toggle
  const handleSortToggle = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);

    // Sort existing pincodes locally
    const sorted = [...pincodes].sort((a, b) => {
      const pinA = parseInt(a.pincode, 10);
      const pinB = parseInt(b.pincode, 10);
      return newOrder === 'asc' ? pinA - pinB : pinB - pinA;
    });
    setPincodes(sorted);
  };

  // Fetch Cities
  const getCities = async () => {
    try {
      const res = await axios.get(CITY_API);
      setCities(res.data.data);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  useEffect(() => {
    getPincodes();
    getCities();
  }, []);

  // Handle Input Change
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Create or Update Pincode
  const savePincode = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingId) {
        await axios.put(`${API}/${editingId}`, form);
      } else {
        await axios.post(API, form);
      }

      setForm({ pincode: "", city: "", state: "", area: "" });
      setEditingId(null);
      getPincodes();
    } catch (err) {
      console.error(err);
      alert("Error saving pincode");
    } finally {
      setSubmitting(false);
    }
  };

  // Edit Mode
  const editPincode = (p) => {
    setEditingId(p.id);
    setForm({
      pincode: p.pincode,
      city: p.city || "",
      state: p.state || "",
      area: p.area || "",
    });
  };

  // Delete
  const deletePincode = async (id) => {
    if (!window.confirm("Are you sure you want to delete this pincode?")) return;

    try {
      await axios.delete(`${API}/${id}`);
      getPincodes();
    } catch (err) {
      console.error("Error deleting pincode:", err);
      alert("Error deleting pincode");
    }
  };

  // Search
  const searchPincode = async (query) => {
    setSearch(query);

    if (query.trim() === "") {
      getPincodes();
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${API}/search/${query}`);
      const sortedData = res.data.data.sort((a, b) => {
        const pinA = parseInt(a.pincode, 10);
        const pinB = parseInt(b.pincode, 10);
        return sortOrder === 'asc' ? pinA - pinB : pinB - pinA;
      });
      setPincodes(sortedData);
    } catch (err) {
      console.error("Error searching:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="">



        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by pincode, area, or city..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              value={search}
              onChange={(e) => searchPincode(e.target.value)}
            />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-800">
              {editingId ? "Edit Pincode" : "Add New Pincode"}
            </h3>
          </div>

          <form onSubmit={savePincode} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Pincode */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Pincode *
                </label>
                <input
                  type="text"
                  name="pincode"
                  placeholder="Enter pincode"
                  value={form.pincode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                  maxLength={6}
                />
              </div>

              {/* City Dropdown */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  City *
                </label>
                <select
                  name="city"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                  value={form.city}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a city</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.name} className="text-sm">
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>


            </div>

            <div className="flex items-center space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </>
                ) : editingId ? "Update Pincode" : "Add Pincode"}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setForm({ pincode: "", city: "", });
                  }}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-base font-medium text-gray-800">Pincode List</h3>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {pincodes.length} records
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-6 py-3 text-left">
                    <button
                      className="flex items-center space-x-1 focus:outline-none"
                      onClick={handleSortToggle}
                      title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                    >
                      <span>Pincode</span>
                      <svg className={`h-4 w-4 transform transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left">City</th>

                  <th className="px-6 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <div className="flex justify-center">
                        <svg className="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      </div>
                      <p className="mt-2 text-sm text-gray-500">Loading pincodes...</p>
                    </td>
                  </tr>
                ) : pincodes.length > 0 ? (
                  pincodes.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{p.pincode}</span>
                      </td>
                      <td className="px-6 py-3">
                        <span className="text-sm text-gray-700">{p.city}</span>
                      </td>

                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => editPincode(p)}
                            className="inline-flex items-center px-3 py-1.5 border border-yellow-400 text-xs font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-yellow-500 transition-colors"
                          >
                            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          <button
                            onClick={() => deletePincode(p.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500 transition-colors"
                          >
                            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <svg className="h-12 w-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="mt-3 text-sm text-gray-500">No pincodes found</p>
                      <p className="text-xs text-gray-400 mt-1">Add a new pincode using the form above</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Showing {pincodes.length} pincode{pincodes.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </div>
  );
}